import React, { useRef, useState } from 'react';
import { ReactComponent as FilterIcon } from '../../../images/filter.svg';
import { ReactComponent as ChevronDown } from '../../../images/chevron-down.svg';
import { FilterModal } from '../../filter-modal/FilterModal';

interface DefaultColumnFilterProps {
  column: {
    filterValue: any;
    preFilteredRows: any;
    setFilter: (value: unknown) => void;
    id: string;
  };
  clearFilterForColumn: (columnId: string) => void;
  currentFiltersRef?: React.RefObject<any[]>;
  setAllFilters?: (filters: any) => void;
  isFullscreen?: boolean;
}

/**
 * Reusable column filter component for data tables
 * Provides a unified filtering interface with filter icon and dropdown modal
 */
export const DefaultColumnFilter: React.FC<DefaultColumnFilterProps> = ({
  column: { filterValue, preFilteredRows, setFilter, id },
  currentFiltersRef,
  setAllFilters,
  isFullscreen = false,
}) => {
  const [showFilter, setShowFilter] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(
    currentFiltersRef?.current?.filter((filter) => filter.columnId === id) || [],
  );
  const triggerRef = useRef<HTMLDivElement>(null);

  // Keep appliedFilters in sync with currentFiltersRef
  //   React.useEffect(() => {
  //     setAppliedFilters(currentFiltersRef?.current?.filter((filter) => filter.columnId === id) || []);
  //   }, [currentFiltersRef?.current, id]);

  const handleFilterToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowFilter((prev) => !prev);
  };

  const handleSetFilter = (value: unknown) => {
    // Prevent closing the modal if the value is the same as the current filter
    if (filterValue === value) {
      setShowFilter(false);
      return;
    }
    setFilter(value);
    // Add the new filter to appliedFilters
    setAppliedFilters((prev) => [
      ...prev.filter((filter) => filter.columnId !== id),
      { columnId: id, filterValue: value },
    ]);
    setTimeout(() => setShowFilter(false), 0); // Close modal after filter is applied and parent re-rendered
  };

  const clearFilterForColumnLocal = (columnId: string) => {
    if (currentFiltersRef && currentFiltersRef.current) {
      // Create a new array for remaining filters (to avoid mutating the ref directly)
      const remainingFilters = (currentFiltersRef.current || []).filter((filter) => filter.columnId !== columnId);
      // Use Object.defineProperty to bypass readonly for currentFiltersRef.current
      Object.defineProperty(currentFiltersRef, 'current', { value: remainingFilters, writable: true });
      if (remainingFilters.length === 0) {
        setAllFilters && setAllFilters([]);
        setAppliedFilters([]);
      } else {
        const updatedFilters = remainingFilters.map((filter: any) => ({
          id: filter.columnId,
          value: filter.filterValue,
        }));
        setAllFilters && setAllFilters(updatedFilters);
        setAppliedFilters(remainingFilters);
      }
    } else {
      // If no currentFiltersRef, just clear filters using setAllFilters if available
      setAllFilters && setAllFilters([]);
      setAppliedFilters([]);
    }
  };
  const handleClose = () => setShowFilter(false);

  return (
    <div
      className='default-column-filter'
      ref={triggerRef}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {appliedFilters?.filter((f) => f.columnId === id).length > 0 ? (
        <FilterIcon onClick={handleFilterToggle} />
      ) : (
        <ChevronDown onClick={handleFilterToggle} />
      )}

      {showFilter && (
        <FilterModal
          columnId={id}
          setFilter={handleSetFilter}
          preFilteredRows={preFilteredRows}
          filterValue={filterValue}
          clearFilterForColumn={clearFilterForColumnLocal}
          onClose={handleClose}
          triggerRef={triggerRef}
          appliedFilters={appliedFilters}
          isFullscreen={isFullscreen}
        />
      )}
    </div>
  );
};
